import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ORDER_STATUSES, PAYMENT_STATUSES, TERMINAL_PAYMENT_STATUSES } from '../firebase/schema';
import { computeOrderDocumentTotals } from './orderTotals';

function paymentSnapshot(order, lineItems = [], options = {}) {
  const totals = computeOrderDocumentTotals(order, lineItems, options);
  return {
    amountDue: totals.total,
    breakdown: totals.paymentBreakdown
  };
}

function hasPaymentBreakdown(payment) {
  const breakdown = payment?.breakdown;
  return (
    breakdown != null &&
    typeof breakdown === 'object' &&
    breakdown.taxableSubtotal != null &&
    breakdown.taxAmount != null &&
    Array.isArray(breakdown.adjustments)
  );
}

/**
 * Invoice-aligned order total (subtotal + upcharges + fees - discount + tax).
 */
export function computeAmountDue(order, lineItems = [], { customer = null } = {}) {
  if (!order) return 0;
  return paymentSnapshot(order, lineItems, { customer }).amountDue;
}

export function buildAwaitingPaymentFields(amountDue, breakdown, existingPayment = {}) {
  return {
    payment: {
      ...existingPayment,
      status: PAYMENT_STATUSES.AWAITING_PAYMENT,
      amountDue,
      breakdown: breakdown || existingPayment.breakdown || null,
      amountPaid: 0,
      paidAt: null,
      paymentDueAt: null,
      paymentRequestedAt: serverTimestamp(),
      stripeCheckoutSessionId: null,
      stripePaymentIntentId: null,
      paymentBatchId: null,
      paidOfflineBy: null,
      paidOfflineNote: null
    }
  };
}

/** Ready To Invoice queue: snapshot amount only; due date set when invoice is sent. */
export function buildInvoiceablePaymentFields(amountDue, breakdown, existingPayment = {}) {
  return {
    payment: {
      ...existingPayment,
      status: PAYMENT_STATUSES.NOT_DUE,
      amountDue,
      breakdown: breakdown || existingPayment.breakdown || null,
      amountPaid: 0,
      paidAt: null,
      paymentDueAt: null,
      paymentRequestedAt: null,
      stripeCheckoutSessionId: null,
      stripePaymentIntentId: null,
      paymentBatchId: null,
      paidOfflineBy: null,
      paidOfflineNote: null
    }
  };
}

export function parsePaymentDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds != null) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Invoice due date for portal display — not the production order.dueDate. */
export function getPaymentDueDate(order) {
  return parsePaymentDate(order?.payment?.paymentDueAt);
}

export function daysPastPaymentDue(order, asOf = new Date()) {
  const due = getPaymentDueDate(order);
  if (!due) return null;
  const today = new Date(asOf);
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - dueDay.getTime()) / (24 * 60 * 60 * 1000));
}

export function isPast30DaysOverdue(order, asOf = new Date()) {
  if (order?.payment?.status !== PAYMENT_STATUSES.AWAITING_PAYMENT) return false;
  const days = daysPastPaymentDue(order, asOf);
  return days != null && days >= 30;
}

/**
 * When an order is Ready To Invoice, snapshot amountDue (status stays not_due until invoiced).
 * Skips if already paid / waived / pending checkout.
 */
export async function syncOrderPaymentIfInvoiceable(db, orderId) {
  if (!orderId) return { skipped: true };

  const orderRef = doc(db, 'orders', orderId);
  const orderSnap = await getDoc(orderRef);
  if (!orderSnap.exists()) return { skipped: true, reason: 'missing_order' };

  const order = { id: orderSnap.id, ...orderSnap.data() };
  if (order.status !== ORDER_STATUSES.READY_TO_INVOICE) {
    return { skipped: true, reason: 'not_ready_to_invoice' };
  }

  const currentStatus = order.payment?.status;
  if (currentStatus && TERMINAL_PAYMENT_STATUSES.includes(currentStatus)) {
    return { skipped: true, reason: 'terminal_payment', currentStatus };
  }
  if (currentStatus === PAYMENT_STATUSES.PENDING) {
    return { skipped: true, reason: 'pending_checkout' };
  }

  const linesSnap = await getDocs(query(collection(db, 'orderLines'), where('orderId', '==', orderId)));
  const lineItems = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  let customer = null;
  if (order.customerId) {
    const customerSnap = await getDoc(doc(db, 'customers', order.customerId));
    if (customerSnap.exists()) {
      customer = { id: customerSnap.id, ...customerSnap.data() };
    }
  }

  const { amountDue, breakdown } = paymentSnapshot(order, lineItems, { customer });

  if (order.payment?.paymentDueAt) {
    if (order.payment?.amountDue === amountDue && hasPaymentBreakdown(order.payment)) {
      return { skipped: true, reason: 'already_invoiced', amountDue };
    }
    await updateDoc(orderRef, {
      payment: {
        ...(order.payment || {}),
        amountDue,
        breakdown
      },
      updatedAt: serverTimestamp()
    });
    return { updated: true, amountDue, reason: 'amount_only' };
  }

  if (
    currentStatus === PAYMENT_STATUSES.AWAITING_PAYMENT &&
    order.payment?.amountDue === amountDue &&
    hasPaymentBreakdown(order.payment)
  ) {
    return { skipped: true, reason: 'already_synced', amountDue };
  }

  await updateDoc(orderRef, {
    ...buildInvoiceablePaymentFields(amountDue, breakdown, order.payment),
    updatedAt: serverTimestamp()
  });

  return { updated: true, amountDue };
}

/**
 * When staff archives/sends an invoice, mark payment due immediately (today).
 */
export async function finalizeOrderPaymentOnInvoice(db, orderId) {
  if (!orderId) return { skipped: true };

  const orderRef = doc(db, 'orders', orderId);
  const orderSnap = await getDoc(orderRef);
  if (!orderSnap.exists()) return { skipped: true, reason: 'missing_order' };

  const order = { id: orderSnap.id, ...orderSnap.data() };
  const currentStatus = order.payment?.status;
  if (currentStatus && TERMINAL_PAYMENT_STATUSES.includes(currentStatus)) {
    return { skipped: true, reason: 'terminal_payment', currentStatus };
  }
  if (currentStatus === PAYMENT_STATUSES.PENDING) {
    return { skipped: true, reason: 'pending_checkout' };
  }

  const linesSnap = await getDocs(query(collection(db, 'orderLines'), where('orderId', '==', orderId)));
  const lineItems = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  let customer = null;
  if (order.customerId) {
    const customerSnap = await getDoc(doc(db, 'customers', order.customerId));
    if (customerSnap.exists()) {
      customer = { id: customerSnap.id, ...customerSnap.data() };
    }
  }

  const { amountDue, breakdown } = paymentSnapshot(order, lineItems, { customer });

  await updateDoc(orderRef, {
    payment: {
      ...(order.payment || {}),
      status: PAYMENT_STATUSES.AWAITING_PAYMENT,
      amountDue,
      breakdown,
      amountPaid: order.payment?.amountPaid ?? 0,
      paidAt: order.payment?.paidAt ?? null,
      paymentDueAt: serverTimestamp(),
      paymentRequestedAt: order.payment?.paymentRequestedAt || serverTimestamp(),
      stripeCheckoutSessionId: order.payment?.stripeCheckoutSessionId ?? null,
      stripePaymentIntentId: order.payment?.stripePaymentIntentId ?? null,
      paymentBatchId: order.payment?.paymentBatchId ?? null,
      paidOfflineBy: order.payment?.paidOfflineBy ?? null,
      paidOfflineNote: order.payment?.paidOfflineNote ?? null
    },
    updatedAt: serverTimestamp()
  });

  return { updated: true, amountDue };
}

/**
 * Rewrite amountDue + breakdown without changing payment status.
 * Used when totals change after payment was requested, and for backfill.
 */
export async function refreshOrderPaymentBreakdown(db, orderId) {
  if (!orderId) return { skipped: true };

  const orderRef = doc(db, 'orders', orderId);
  const orderSnap = await getDoc(orderRef);
  if (!orderSnap.exists()) return { skipped: true, reason: 'missing_order' };

  const order = { id: orderSnap.id, ...orderSnap.data() };
  const currentStatus = order.payment?.status;
  if (currentStatus && TERMINAL_PAYMENT_STATUSES.includes(currentStatus)) {
    return { skipped: true, reason: 'terminal_payment', currentStatus };
  }
  if (currentStatus === PAYMENT_STATUSES.PENDING) {
    return { skipped: true, reason: 'pending_checkout' };
  }

  const linesSnap = await getDocs(query(collection(db, 'orderLines'), where('orderId', '==', orderId)));
  const lineItems = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  let customer = null;
  if (order.customerId) {
    const customerSnap = await getDoc(doc(db, 'customers', order.customerId));
    if (customerSnap.exists()) {
      customer = { id: customerSnap.id, ...customerSnap.data() };
    }
  }

  const { amountDue, breakdown } = paymentSnapshot(order, lineItems, { customer });

  await updateDoc(orderRef, {
    payment: {
      ...(order.payment || {}),
      amountDue,
      breakdown
    },
    updatedAt: serverTimestamp()
  });

  return { updated: true, amountDue };
}

export async function markOrderPaidOffline(db, orderId, { staffEmail, note = '' } = {}) {
  if (!orderId) throw new Error('orderId required');

  const orderRef = doc(db, 'orders', orderId);
  const orderSnap = await getDoc(orderRef);
  if (!orderSnap.exists()) throw new Error('Order not found');

  const order = orderSnap.data();
  const amountDue = order.payment?.amountDue ?? 0;

  await updateDoc(orderRef, {
    payment: {
      ...(order.payment || {}),
      status: PAYMENT_STATUSES.PAID_OFFLINE,
      amountPaid: amountDue,
      paidAt: serverTimestamp(),
      paidOfflineBy: staffEmail || null,
      paidOfflineNote: note || null
    },
    updatedAt: serverTimestamp()
  });
}
